import { useMemo, useState } from "react";
import { Plus, Trash2, Printer, FileDown, Pencil, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, Stat } from "@/components/Bits";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApp } from "@/store/AppStore";
import { fmtNum, fmtInt, fmtBags } from "@/lib/format";
import { PrintSheet } from "@/components/PrintSheet";

function consolidate(lines) {
  const bolts = {}, nuts = {}, washers = {};
  const add = (map, key, label, size, pcs, kg) => {
    if (!map[key]) map[key] = { label, size, pcs: 0, kg: 0 };
    map[key].pcs += pcs; map[key].kg += kg;
  };
  lines.forEach((l) => {
    add(bolts, l.bolt.key, l.bolt.label, l.bolt.size, l.bolt.pcs, l.bolt.kg);
    add(nuts, l.nut.key, l.nut.label, l.nut.size, l.nut.pcs, l.nut.kg);
    if (l.washer?.enabled) add(washers, l.washer.key, l.washer.label, l.washer.label, l.washer.pcs, l.washer.kg);
  });
  const g = (m) => Object.values(m);
  const arr = { bolts: g(bolts), nuts: g(nuts), washers: g(washers) };
  const totalKg = [...arr.bolts, ...arr.nuts, ...arr.washers].reduce((s, x) => s + x.kg, 0);
  return { ...arr, totalKg, totalBags: totalKg / 50 };
}

export function OrderPage() {
  const { orders, activeOrder, activeOrderId, setActiveOrderId, createOrder, deleteOrder, renameOrder, deleteLine } = useApp();
  const [openLines, setOpenLines] = useState(true);
  const [nameDlg, setNameDlg] = useState({ open: false, mode: "create", value: "", id: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const lines = activeOrder?.lines || [];
  const data = useMemo(() => consolidate(lines), [lines]);

  const openCreate = () => setNameDlg({ open: true, mode: "create", value: "", id: null });
  const openRename = (o) => setNameDlg({ open: true, mode: "rename", value: o.name, id: o.id });
  const submitName = async () => {
    const val = nameDlg.value.trim();
    setNameDlg((d) => ({ ...d, open: false }));
    if (nameDlg.mode === "create") {
      const o = await createOrder(val || undefined);
      if (o) toast.success("Order created");
    } else if (val) {
      await renameOrder(nameDlg.id, val);
      toast.success("Order renamed");
    }
  };
  const confirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (target) {
      await deleteOrder(target.id);
      toast.success(`Deleted "${target.name}"`);
    }
  };

  return (
    <div className="space-y-4">
      {/* Order switcher */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
        {orders.map((o) => (
          <div
            key={o.id}
            className={`flex shrink-0 items-center gap-1 rounded-xl border pl-3 pr-1 py-1 text-sm font-semibold ${o.id === activeOrderId ? "border-orange-500 bg-orange-500 text-slate-950" : "border-slate-700 bg-slate-900/50 text-slate-300"}`}
          >
            <button data-testid={`order-tab-${o.id}`} onClick={() => setActiveOrderId(o.id)} className="py-1">
              {o.name} <span className="opacity-60">({o.lines.length})</span>
            </button>
            <button
              data-testid={`order-tab-delete-${o.id}`}
              onClick={() => setDeleteTarget(o)}
              aria-label={`Delete ${o.name}`}
              className={`rounded-md p-1 ${o.id === activeOrderId ? "hover:bg-black/15 text-slate-900" : "text-slate-500 hover:bg-red-500/15 hover:text-red-400"}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button data-testid="new-order-btn" onClick={openCreate} className="shrink-0 rounded-xl border border-dashed border-slate-600 px-3 py-2 text-sm font-semibold text-slate-400 hover:text-white">
          <Plus className="inline h-4 w-4" /> New
        </button>
      </div>

      {!activeOrder ? (
        <Card className="py-12 text-center">
          <p className="text-slate-400">No order selected.</p>
          <p className="mt-1 text-sm text-slate-600">Create an order, then build sets in the ASSEMBLY tab.</p>
          <Button onClick={openCreate} data-testid="empty-create-order-btn" className="mt-4 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">
            <Plus className="mr-1 h-4 w-4" /> Create Order
          </Button>
        </Card>
      ) : (
        <>
          {/* Order header + actions */}
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-lg font-extrabold text-white" data-testid="active-order-name">{activeOrder.name}</h2>
                  <button onClick={() => openRename(activeOrder)} data-testid="rename-order-btn" className="text-slate-500 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                </div>
                <div className="text-xs text-slate-500">{lines.length} line(s)</div>
              </div>
              <button onClick={() => setDeleteTarget(activeOrder)} data-testid="delete-order-btn" className="rounded-lg p-2 text-slate-500 hover:bg-red-500/10 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Stat label="Grand Total" value={fmtNum(data.totalKg, 1)} unit="KG" testid="order-total-kg" accent />
              <Stat label="Total Bags" value={fmtBags(data.totalBags)} testid="order-total-bags" />
            </div>
            {lines.length > 0 && (
              <div className="mt-3 flex gap-2">
                <Button data-testid="print-order-btn" onClick={() => window.print()} className="flex-1 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">
                  <Printer className="mr-1.5 h-4 w-4" /> Print
                </Button>
                <Button data-testid="save-pdf-btn" onClick={() => window.print()} variant="outline" className="flex-1 rounded-xl border-slate-700 text-slate-200">
                  <FileDown className="mr-1.5 h-4 w-4" /> Save PDF
                </Button>
              </div>
            )}
          </Card>

          {lines.length === 0 ? (
            <Card className="py-10 text-center">
              <p className="text-slate-400">No material added yet.</p>
              <p className="mt-1 text-sm text-slate-600">Build sets in the ASSEMBLY tab and tap “Add to Order”.</p>
            </Card>
          ) : (
            <>
              {data.bolts.length > 0 && <MatSection title="BOLTS" rows={data.bolts} testid="order-bolts" />}
              {data.nuts.length > 0 && <MatSection title="NUTS" rows={data.nuts} testid="order-nuts" />}
              {data.washers.length > 0 && <MatSection title="WASHERS" rows={data.washers} testid="order-washers" />}

              {/* Added lines */}
              <Collapsible open={openLines} onOpenChange={setOpenLines}>
                <Card>
              <CollapsibleTrigger asChild>
                <button className="flex w-full items-center justify-between" data-testid="toggle-lines">
                  <span className="text-sm font-bold uppercase tracking-wider text-slate-300">Added Lines ({lines.length})</span>
                  <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${openLines ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 space-y-2">
                {lines.map((l) => {
                  const lineBags = l.bolt.bags + l.nut.bags + (l.washer?.bags || 0);
                  return (
                  <div key={l.id} data-testid={`order-line-${l.id}`} className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-100">{l.bolt.size}</div>
                      <div className="truncate text-[11px] text-slate-500">{l.assemblyText}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-lg font-extrabold leading-none text-orange-300" data-testid={`order-line-bags-${l.id}`}>
                        {fmtBags(lineBags)}<span className="ml-1 text-[10px] font-semibold text-slate-500">BAGS</span>
                      </div>
                      <div className="mt-1 font-mono text-[11px] text-slate-500" data-testid={`order-line-sets-${l.id}`}>{fmtInt(l.sets)} sets</div>
                    </div>
                    <button onClick={() => deleteLine(activeOrder.id, l.id)} data-testid={`delete-line-${l.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-red-500/10 hover:text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  );
                })}
              </CollapsibleContent>
            </Card>
          </Collapsible>
            </>
          )}
        </>
      )}

      {activeOrder && lines.length > 0 && <PrintSheet order={activeOrder} data={data} />}

      {/* Create / Rename order dialog */}
      <Dialog open={nameDlg.open} onOpenChange={(o) => setNameDlg((d) => ({ ...d, open: o }))}>
        <DialogContent className="border-slate-700 bg-[#131C2E] text-slate-100" data-testid="order-name-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{nameDlg.mode === "create" ? "New Order" : "Rename Order"}</DialogTitle>
            <DialogDescription className="text-slate-400">Give this order a name you'll recognise.</DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            data-testid="order-name-input"
            value={nameDlg.value}
            onChange={(e) => setNameDlg((d) => ({ ...d, value: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && submitName()}
            placeholder={`e.g. Order ${orders.length + 1}`}
            className="h-11 rounded-xl border-slate-700 bg-slate-900/60 text-white"
          />
          <DialogFooter>
            <Button data-testid="order-name-submit" onClick={submitName} className="rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">
              {nameDlg.mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete order confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-slate-700 bg-[#131C2E] text-slate-100" data-testid="delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this order?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              “{deleteTarget?.name}” and all its {deleteTarget?.lines?.length || 0} line(s) will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-order-cancel" className="rounded-xl border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-order-confirm" onClick={confirmDelete} className="rounded-xl bg-red-500 font-bold text-white hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MatSection({ title, rows, testid }) {
  return (
    <Card className="p-0 overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between bg-slate-800/60 px-4 py-2">
        <span className="text-xs font-bold uppercase tracking-wider text-orange-300">{title}</span>
        <span className="text-[10px] font-semibold uppercase text-slate-500">PCS · KG · BAGS</span>
      </div>
      <div className="divide-y divide-slate-800">
        {rows.map((r) => (
          <div key={r.size} className="flex items-center gap-3 px-4 py-2.5">
            <span className="font-mono text-sm font-bold text-cyan-400">{r.size}</span>
            <div className="ml-auto flex items-center gap-3 font-mono text-sm">
              <span className="text-white">{fmtInt(r.pcs)}</span>
              <span className="text-slate-500">·</span>
              <span className="text-white">{fmtNum(r.kg, 1)}</span>
              <span className="text-slate-500">·</span>
              <span className="text-orange-300">{fmtBags(r.kg / 50)}</span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
